import React, { useState, useEffect, useCallback, useMemo, useContext, createContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import {
  DataGridPremium,
  GridToolbar,
  GridLinkOperator,
  useGridApiRef,
  nlNL,
} from '@mui/x-data-grid-premium';
import { prepareColumns as prepareGridColumns } from '../../utilities/gridColumnUtilities';
import { getDataTable } from '../../selectors/dataTableSelectors';
import { getUsers } from '../../selectors/userSelectors';
import { extractGridMetadata } from '../../utilities/gridMetadataExtractor';
import { executeSearch } from '../../utilities/gridSearchHandler';
import { convertFilterModelToBackendQuery } from '../../utilities/gridFilterConverter';
import { convertSortModelToBackend } from '../../utilities/gridSortUtilities';
import {
  getRowsSelector,
  getTotalSelector,
  getLoadingSelector
} from '../../utilities/gridCollectionMapper';
import { useGridState } from '../../hooks/useGridState';
import { createPaginationHandlers } from '../../utilities/gridPaginationUtilities';
import { fetchDataTable } from '../../actions/dataTableActions';
import { getRequiredTableIds, hasUserFields } from '../../utilities/gridReferenceTableLoader';
import { fetchUsers } from '../../actions/userActions';

const GridLoadingContext = createContext(false);

const NoRowsOverlay = () => {
  const loading = useContext(GridLoadingContext);
  if (loading) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" color="text.secondary">
        Geen resultaten
      </Typography>
    </Box>
  );
};

const hasActiveFilters = (filterModel) => {
  return (
    (filterModel?.items && filterModel.items.length > 0) ||
    (filterModel?.quickFilterValues && filterModel.quickFilterValues.length > 0)
  );
};

const FilterWarningOverlay = ({ onClearFilters }) => {
  const [headerHeight, setHeaderHeight] = React.useState(56);

  React.useEffect(() => {
    const calculateHeaderHeight = () => {
      const dataGrid = document.querySelector('.MuiDataGrid-root');
      if (dataGrid) {
        const toolbarSelectors = [
          '.MuiDataGrid-toolbarContainer',
          '.MuiDataGrid-toolbar',
          '[class*="toolbar"]',
          '.MuiDataGrid-main > div:first-child'
        ];

        const columnHeaders = dataGrid.querySelector('.MuiDataGrid-columnHeaders');

        let totalHeight = 0;

        for (const selector of toolbarSelectors) {
          const toolbar = dataGrid.querySelector(selector);
          if (toolbar) {
            const rect = toolbar.getBoundingClientRect();
            if (rect.height > 0) {
              totalHeight += rect.height;
              break;
            }
          }
        }

        if (columnHeaders) {
          const headersHeight = columnHeaders.getBoundingClientRect().height;
          totalHeight += headersHeight;
        }

        setHeaderHeight(totalHeight || 56);
      }
    };

    calculateHeaderHeight();
    window.addEventListener('resize', calculateHeaderHeight);

    const timers = [
      setTimeout(calculateHeaderHeight, 100),
      setTimeout(calculateHeaderHeight, 300),
      setTimeout(calculateHeaderHeight, 500)
    ];

    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: `${headerHeight + 100}px`,
        left: 0,
        right: 0,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 1,
          mt: 1,
          backgroundColor: 'rgba(255, 152, 0, 0.95)',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'warning.main',
          boxShadow: 2,
          pointerEvents: 'auto',
          maxWidth: 400,
        }}
      >
        <Typography variant="body2" color="white" sx={{ fontWeight: 500 }}>
          ⚠️ No records match your filters
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onClearFilters}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.7)',
            minWidth: 'auto',
            fontSize: '0.75rem',
            py: 0.5,
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          Clear Filters
        </Button>
      </Box>
    </Box>
  );
};

const StandardDataGrid = ({
  collection,
  columnDefinitions,
  onRowClick,
  showToolbar = true,
  autoHeight = false,
  checkboxSelection = false,
  disableRowSelectionOnClick = true,
  customSx = {},
  pageSizeOptions = [10, 25, 50, 100],
  paginationMode = 'server',
  sortingMode = 'server',
  filterMode = 'server',
  aggregate = true,
  ...props
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // Extract metadata from column definitions
  const { populateFields, customComponents } = useMemo(
    () => extractGridMetadata(columnDefinitions),
    [columnDefinitions]
  );

  // Get all users for user field dropdowns
  const allUsers = useSelector(getUsers);

  // Get selectors for this collection
  const rowsSelector = useMemo(() => getRowsSelector(collection), [collection]);
  const totalSelector = useMemo(() => getTotalSelector(collection), [collection]);
  const loadingSelector = useMemo(() => getLoadingSelector(collection), [collection]);

  // Get data from Redux based on collection name
  const rows = useSelector(rowsSelector);
  const totalCount = useSelector(totalSelector);
  const loading = useSelector(loadingSelector);

  // Extract default visible columns from column definitions
  const defaultVisibleColumns = useMemo(() => {
    if (!columnDefinitions) return [];
    return columnDefinitions
      .filter(col => col.defaultVisible === true)
      .map(col => col.value);
  }, [columnDefinitions]);

  const apiRef = useGridApiRef();
  const [showFilterWarning, setShowFilterWarning] = useState(false);

  // Compute default column visibility from columnDefinitions
  const defaultColumnVisibilityModel = useMemo(() => {
    const visibility = {};
    columnDefinitions?.forEach(col => {
      visibility[col.value] = defaultVisibleColumns.includes(col.value);
    });
    return visibility;
  }, [columnDefinitions, defaultVisibleColumns]);

  // Use grid state hook for persistence
  const {
    filterModel,
    sortModel,
    paginationModel,
    columnVisibilityModel,
    setFilterModel,
    setSortModel,
    setPaginationModel,
    setColumnVisibilityModel,
    saveGridState,
    saveColumnVisibility
  } = useGridState(
    `${collection}GridState`,
    `${collection}ColumnVisibility`,
    defaultColumnVisibilityModel
  );

  // Initialize column visibility in localStorage if it doesn't exist
  // This ensures saved list views have proper defaults
  useEffect(() => {
    const columnVisibilityKey = `${collection}ColumnVisibility`;
    const stored = localStorage.getItem(columnVisibilityKey);

    if (!stored) {
      // No column visibility saved yet - initialize with defaults
      localStorage.setItem(columnVisibilityKey, JSON.stringify(defaultColumnVisibilityModel));
    }
  }, [collection, defaultColumnVisibilityModel]);

  // Get required table IDs based on visible columns only
  const requiredTableIds = useMemo(() => {
    return getRequiredTableIds(columnDefinitions, columnVisibilityModel);
  }, [columnDefinitions, columnVisibilityModel]);

  // Check if any visible columns are user fields
  const needsUserData = useMemo(() => {
    return hasUserFields(columnDefinitions, columnVisibilityModel);
  }, [columnDefinitions, columnVisibilityModel]);

  // Fetch all required data tables from Redux
  const enrichedDataTables = useSelector(state => {
    const tables = {};
    requiredTableIds.forEach(tableId => {
      tables[tableId] = getDataTable(state, tableId);
    });
    return tables;
  });

  // Fetch required reference tables when visible columns change
  useEffect(() => {
    requiredTableIds.forEach(tableId => {
      dispatch(fetchDataTable(tableId));
    });
  }, [dispatch, requiredTableIds]);

  // Fetch all users for user field dropdowns (manager, _createdBy, etc.) only if needed
  useEffect(() => {
    if (needsUserData) {
      // Fetch all users with a large limit to ensure all are available for dropdowns
      dispatch(fetchUsers({ limit: 1000, offset: 0 }));
    }
  }, [dispatch, needsUserData]);

  // Prepare columns from columnDefinitions if provided
  // This must be after enrichedDataTables is defined
  const preparedColumns = useMemo(() => {
    if (columnDefinitions) {
      return prepareGridColumns(columnDefinitions, {
        allUsers,
        dataTables: enrichedDataTables,
        intl,
        customComponents
      });
    }
    return [];
  }, [columnDefinitions, allUsers, enrichedDataTables, intl, customComponents]);

  // Create pagination handlers using utility
  const { handlePageChange, handlePageSizeChange } = useMemo(
    () => createPaginationHandlers(
      setPaginationModel,
      saveGridState,
      (newPaginationModel) => {
        // Trigger search when pagination changes
        setTimeout(() => {
          const { sortField, sortDirection } = convertSortModelToBackend(sortModel, columnDefinitions);
          const filterQuery = convertFilterModelToBackendQuery(filterModel, columnDefinitions, {});
          const visible = Object.keys(columnVisibilityModel).filter(k => columnVisibilityModel[k]);

          executeSearch(dispatch, collection, {
            filterQuery,
            sortField,
            sortDirection,
            pageSize: newPaginationModel.pageSize,
            page: newPaginationModel.page,
            visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
            populateFields,
            aggregate
          });
        }, 0);
      }
    ),
    [setPaginationModel, saveGridState, sortModel, filterModel, columnDefinitions, columnVisibilityModel, defaultVisibleColumns, populateFields, dispatch, collection, aggregate]
  );

  // Execute search with current grid state
  const executeGridSearch = useCallback((overrides = {}) => {
    const { sortField, sortDirection } = convertSortModelToBackend(sortModel, columnDefinitions);
    const filterQuery = convertFilterModelToBackendQuery(filterModel, columnDefinitions, {});

    const visible = Object.keys(columnVisibilityModel).filter(k => columnVisibilityModel[k]);

    const gridState = {
      filterQuery,
      sortField,
      sortDirection,
      pageSize: paginationModel.pageSize,
      page: paginationModel.page,
      visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
      populateFields,
      aggregate,
      ...overrides
    };

    executeSearch(dispatch, collection, gridState);
  }, [dispatch, collection, filterModel, sortModel, paginationModel, columnVisibilityModel, columnDefinitions, populateFields, aggregate]);

  const clearFilters = useCallback(() => {
    const emptyFilterModel = {
      items: [],
      linkOperator: GridLinkOperator.And,
      quickFilterValues: [],
      quickFilterLogicOperator: GridLinkOperator.And
    };

    setFilterModel(emptyFilterModel);
    setShowFilterWarning(false);

    // Execute search with empty filters
    setTimeout(() => {
      const { sortField, sortDirection } = convertSortModelToBackend(sortModel, columnDefinitions);
      const visible = Object.keys(columnVisibilityModel).filter(k => columnVisibilityModel[k]);

      executeSearch(dispatch, collection, {
        filterQuery: [],
        sortField,
        sortDirection,
        pageSize: paginationModel.pageSize,
        page: paginationModel.page,
        visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
        populateFields,
        aggregate
      });
    }, 0);
  }, [dispatch, collection, sortModel, columnDefinitions, paginationModel, columnVisibilityModel, defaultVisibleColumns, populateFields, aggregate]);

  const handleFilterModelChange = useCallback((model) => {
    setFilterModel(model);
    saveGridState({ filterModel: model });

    const hasFilters = hasActiveFilters(model);

    if (!hasFilters) {
      setShowFilterWarning(false);
    } else {
      setTimeout(() => {
        checkForNoResults();
      }, 300);
    }

    // Execute search with new filters
    setTimeout(() => {
      const { sortField, sortDirection } = convertSortModelToBackend(sortModel, columnDefinitions);
      const filterQuery = convertFilterModelToBackendQuery(model, columnDefinitions, {});
      const visible = Object.keys(columnVisibilityModel).filter(k => columnVisibilityModel[k]);

      executeSearch(dispatch, collection, {
        filterQuery,
        sortField,
        sortDirection,
        pageSize: paginationModel.pageSize,
        page: 0, // Reset to first page on filter change
        visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
        populateFields,
        aggregate
      });

      // Also reset pagination
      const newPaginationModel = { ...paginationModel, page: 0 };
      setPaginationModel(newPaginationModel);
      saveGridState({ paginationModel: newPaginationModel });
    }, 0);
  }, [dispatch, collection, sortModel, columnDefinitions, columnVisibilityModel, paginationModel, defaultVisibleColumns, populateFields, saveGridState, aggregate]);

  const handleSortModelChange = useCallback((model) => {
    setSortModel(model);
    saveGridState({ sortModel: model });

    // Execute search with new sort
    setTimeout(() => {
      const { sortField, sortDirection } = convertSortModelToBackend(model, columnDefinitions);
      const filterQuery = convertFilterModelToBackendQuery(filterModel, columnDefinitions, {});
      const visible = Object.keys(columnVisibilityModel).filter(k => columnVisibilityModel[k]);

      executeSearch(dispatch, collection, {
        filterQuery,
        sortField,
        sortDirection,
        pageSize: paginationModel.pageSize,
        page: paginationModel.page,
        visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
        populateFields,
        aggregate
      });
    }, 0);
  }, [dispatch, collection, filterModel, columnDefinitions, columnVisibilityModel, paginationModel, defaultVisibleColumns, populateFields, saveGridState, aggregate]);

  const handleColumnVisibilityModelChange = useCallback((model) => {
    setColumnVisibilityModel(model);

    // Create a complete visibility model with ALL columns explicitly set
    // This ensures saved list views have complete information
    const completeModel = {};
    columnDefinitions?.forEach(col => {
      // Use the value from the incoming model if present, otherwise default to true (visible)
      completeModel[col.value] = model[col.value] !== undefined ? model[col.value] : true;
    });

    saveColumnVisibility(completeModel);

    // Execute search with new visible columns
    setTimeout(() => {
      const { sortField, sortDirection } = convertSortModelToBackend(sortModel, columnDefinitions);
      const filterQuery = convertFilterModelToBackendQuery(filterModel, columnDefinitions, {});
      const visible = Object.keys(model).filter(k => model[k]);

      executeSearch(dispatch, collection, {
        filterQuery,
        sortField,
        sortDirection,
        pageSize: paginationModel.pageSize,
        page: paginationModel.page,
        visibleColumns: visible.length > 0 ? visible : defaultVisibleColumns,
        populateFields,
        aggregate
      });
    }, 0);
  }, [dispatch, collection, sortModel, filterModel, columnDefinitions, paginationModel, defaultVisibleColumns, populateFields, saveColumnVisibility, aggregate]);

  const checkForNoResults = () => {
    try {
      let dataGridElement = apiRef.current?.rootElementRef?.current;

      if (!dataGridElement) {
        dataGridElement = document.querySelector('.MuiDataGrid-root');
      }

      if (dataGridElement) {
        const overlaySelectors = [
          '.MuiDataGrid-overlay',
          '.MuiDataGrid-overlayWrapper',
          '.MuiDataGrid-main .MuiDataGrid-overlay',
          '[class*="overlay"]',
        ];

        let noResultsText = null;

        for (const selector of overlaySelectors) {
          noResultsText = dataGridElement.querySelector(selector);
          if (noResultsText) {
            break;
          }
        }

        const hasNoResultsOverlay = noResultsText && (
          noResultsText.textContent?.includes('No results found') ||
          noResultsText.textContent?.includes('No rows') ||
          noResultsText.textContent?.includes('no data') ||
          noResultsText.textContent?.includes('No data')
        );

        const hasFilters = hasActiveFilters(filterModel);
        const shouldShow = hasFilters && !!hasNoResultsOverlay && !loading;

        setShowFilterWarning(shouldShow);
      }
    } catch (error) {
      // Silently fail - warning banner won't show
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const hasFilters = hasActiveFilters(filterModel);
      if (hasFilters) {
        checkForNoResults();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (rows && rows.length > 0) {
      const timer = setTimeout(() => {
        const hasFilters = hasActiveFilters(filterModel);
        if (hasFilters) {
          checkForNoResults();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [rows]);

  // Execute initial search on mount
  useEffect(() => {
    executeGridSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Only run on mount

  // Re-execute search when aggregate changes
  useEffect(() => {
    // Skip on initial mount (handled by previous useEffect)
    if (paginationModel.page !== undefined) {
      executeGridSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate]);

  const shouldShowFilterWarning = () => {
    return showFilterWarning && !loading;
  };

  const buildSx = () => {
    return {
      // Match AllowanceList exact font family and size
      fontFamily: 'ROsanswebtext, Calibri, sans-serif',
      fontSize: '15px',
      lineHeight: '20px',
      '& .MuiDataGrid-cell': {
        fontFamily: 'inherit',
        fontSize: '15px',
        padding: '12px 5px', // Match old table: 0.75rem 5px
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      },
      '& .MuiDataGrid-columnHeaders': {
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: 'bold',
        color: '#999', // lightgray text color
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      },
      '& .MuiDataGrid-columnHeader': {
        padding: '8px 5px',
        '&:focus': {
          outline: 'none',
        },
        '&:focus-within': {
          outline: 'none',
        },
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontFamily: 'inherit',
        fontSize: '15px',
        fontWeight: 'bold',
        lineHeight: '20px',
      },
      '& .MuiDataGrid-row': {
        '&:hover': {
          cursor: onRowClick ? 'pointer' : 'default',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
      '& .MuiDataGrid-cell:focus': {
        outline: 'none',
      },
      '& .MuiDataGrid-cell:focus-within': {
        outline: 'none',
      },
      // Remove grid borders to match old table
      border: 0,
      '& .MuiDataGrid-main': {
        border: 0,
      },
      '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        justifyContent: 'flex-start', // Move pagination to the left
        '& .MuiTablePagination-root': {
          fontFamily: 'inherit',
          fontSize: '15px',
          marginLeft: 0, // Remove left margin
        },
        '& .MuiTablePagination-toolbar': {
          paddingLeft: '16px', // Add padding for consistency
          alignItems: 'center', // Vertically align all items
          minHeight: '52px', // Standard height for pagination
        },
        '& .MuiTablePagination-selectLabel': {
          margin: 0, // Remove default margin
        },
        '& .MuiTablePagination-displayedRows': {
          margin: 0, // Remove default margin
        },
        '& .MuiTablePagination-select': {
          marginTop: 0, // Remove top margin from select
          marginBottom: 0, // Remove bottom margin from select
        },
        '& .MuiTablePagination-spacer': {
          display: 'none', // Remove spacer that pushes content apart
        },
      },
      // Toolbar font consistency
      '& .MuiDataGrid-toolbarContainer': {
        fontFamily: 'inherit',
        fontSize: '15px',
        '& .MuiButton-root': {
          fontFamily: 'inherit',
          fontSize: '14px',
        },
        '& .MuiInputBase-root': {
          fontFamily: 'inherit',
          fontSize: '14px',
        },
      },
      // Force filter panel to have proper width and positioning
      '& .MuiDataGrid-panel': {
        minWidth: '1600px !important',
        width: '1600px !important',
        maxWidth: '95vw !important',
        left: '50% !important',
        transform: 'translateX(-50%) !important',
        position: 'fixed !important',
        top: '15% !important',
        zIndex: '9999 !important',
        overflow: 'visible !important',
      },
      '& .MuiDataGrid-panelWrapper': {
        overflow: 'visible !important',
      },
      '& .MuiDataGrid-panelContent': {
        width: '100% !important',
        overflow: 'visible !important',
        padding: '24px !important',
      },
      '& .MuiDataGrid-filterForm': {
        display: 'flex !important',
        flexWrap: 'nowrap !important',
        gap: '12px !important',
        width: '100% !important',
        alignItems: 'center !important',
        overflow: 'visible !important',
      },
      '& .MuiDataGrid-filterFormColumnInput': {
        minWidth: '180px !important',
        width: '180px !important',
        flexShrink: 0,
        display: 'flex !important',
        alignItems: 'center !important',
      },
      '& .MuiDataGrid-filterFormOperatorInput': {
        minWidth: '200px !important',
        width: '200px !important',
        flexShrink: 0,
        display: 'flex !important',
        alignItems: 'center !important',
      },
      '& .MuiDataGrid-filterFormValueInput': {
        minWidth: '1000px !important',
        width: '1000px !important',
        flexShrink: 0,
        overflow: 'visible !important',
        display: 'flex !important',
        alignItems: 'center !important',
        '& > div': {
          width: '100% !important',
          overflow: 'visible !important',
          display: 'flex !important',
          alignItems: 'center !important',
        },
        '& input, & select': {
          maxWidth: '100% !important',
        },
      },
      '& .MuiDataGrid-filterFormDeleteIcon': {
        flexShrink: 0,
        marginLeft: '8px !important',
        display: 'flex !important',
        alignItems: 'center !important',
      },
      // Hide the "Groepeer op" (link operator) section in filter panel
      '& .MuiDataGrid-filterFormLinkOperatorInput': {
        display: 'none !important',
      },
      '& .MuiDataGrid-panelFooter': {
        '& > div:first-of-type': {
          display: 'none !important',
        },
      },
      ...customSx,
    };
  };

  const processedColumns = preparedColumns.map(col => ({
    ...col,
    field: col.field || col.name || col.value,
    headerName: col.headerName || col.title || col.label || col.name,
    flex: col.flex !== undefined ? col.flex : (col.width ? undefined : 1),
    width: col.width,
    sortable: col.sortable !== false,
    filterable: col.filterable !== false,
  }));

  const getRowId = (row) => {
    if (row.key !== undefined) {
      return row.key;
    }
    return row._id || row.id || row.tableId;
  };

  const handleRowClick = (params) => {
    if (onRowClick) {
      onRowClick(params.row);
    }
  };

  return (
    <GridLoadingContext.Provider value={loading}>
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: autoHeight ? 'auto' : '100%',
        flex: autoHeight ? 'none' : '1 1 auto',
        minHeight: autoHeight ? 0 : 400,
        display: 'flex',
        flexDirection: 'column',
        ...(shouldShowFilterWarning() && {
          '& .MuiDataGrid-overlay': {
            display: 'none !important',
          },
          '& .MuiDataGrid-overlayWrapper': {
            display: 'none !important',
          }
        })
      }}
    >
      <DataGridPremium
        apiRef={apiRef}
        rows={rows}
        columns={processedColumns}
        getRowId={getRowId}
        loading={loading}
        localeText={nlNL.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          ...buildSx(),
          flex: autoHeight ? 'none' : '1 1 auto',
          height: autoHeight ? 'auto' : '100%'
        }}
        pageSize={paginationModel.pageSize}
        page={paginationModel.page}
        rowsPerPageOptions={pageSizeOptions || [25, 50, 100]}
        pagination
        paginationMode={paginationMode}
        sortingMode={sortingMode}
        filterMode={filterMode}
        rowCount={totalCount}

        // Pagination handlers (MUI v5)
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}

        // Sort
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}

        // Filter
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}

        // Column visibility
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityModelChange}

        // Row click
        onRowClick={handleRowClick}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        autoHeight={autoHeight}
        slots={{
          loadingOverlay: LinearProgress,
          noRowsOverlay: NoRowsOverlay,
          toolbar: showToolbar ? GridToolbar : null,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        {...props}
      />
      {shouldShowFilterWarning() && (
        <FilterWarningOverlay onClearFilters={clearFilters} />
      )}
    </Box>
    </GridLoadingContext.Provider>
  );
};

export default StandardDataGrid;
